import { prejudicesData } from '@norma/shared/constants/prejudices';
import { fCurrency } from '@norma/shared/helpers/formatNumber';
import { fDate } from '@norma/shared/helpers/formatTime';
import { MonetaryErosion } from '@norma/shared/types/monetaryErosion.type';
import {
  Prejudice,
  PrejudiceFormCalendrierValeur,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Paragraph, Table, TableOfContents } from 'docx';
import i18next from 'i18next';
import { sortBy } from 'lodash';
import { fPartResponsabilite } from 'src/helpers/formatValues';
import { displayNumeroPiece } from 'src/helpers/prejudices/numeroPiece';
import { getEmptyLine, getParagraph, getTextRun } from '../../docxFunctions';
import { prejudiceCalculsPrint } from '../prejudiceCalculsPrint';
import { prejudiceFormTable } from '../prejudiceFormTablePrint';
import { prejudiceNotesPrint } from '../prejudiceNotesPrint';
import { getMontantRevalorisePrint } from '../revalorisationPrint';

export const calendrierValeursPrint = ({
  formData,
  monetaryErosions,
  partResponsabilite,
  prejudice,
  procedure,
}: {
  formData: PrejudiceFormCalendrierValeur;
  monetaryErosions: MonetaryErosion[];
  partResponsabilite: number;
  prejudice: Pick<Prejudice, 'formData' | 'formType' | 'type'>;
  procedure: Procedure;
}): (Paragraph | Table | TableOfContents)[] => {
  const table = prejudiceFormTable(
    sortBy(formData.rows, (row) => row.date),
    [
      {
        name: 'numerosPieces',
        headerLabel: i18next.t('numeroPiece.form.columnHeader'),
        render: (row) =>
          row.numerosPieces ? displayNumeroPiece(row.numerosPieces) : '',
      },
      {
        name: 'intitule',
        headerLabel: i18next.t(
          'prejudice.prejudicesFormTypes.CALENDRIER_VALEURS.fields.intitule.columnHeader',
        ),
      },
      {
        name: 'date',
        headerLabel: i18next.t(
          'prejudice.prejudicesFormTypes.CALENDRIER_VALEURS.fields.date.columnHeader',
        ),
        render: (row) => (row.date ? fDate(row.date) : ''),
      },
      {
        name: 'montant',
        headerLabel: 'Montant',
        render: (row) =>
          !prejudicesData[prejudice.type || 'DEPENSE_SANTE_FUTURES']
            .noRevalorisation
            ? getMontantRevalorisePrint({
                montant: row.montant,
                anneeOrDateLiquidation: procedure.dateLiquidation || undefined,
                monetaryErosions,
                anneeOrDateMontant: row.date || undefined,
                montantsDejaRevalorises: row.montantsDejaRevalorises || true,
              })
            : fCurrency(row.montant),
      },
    ],
  );
  const notesPrint = prejudiceNotesPrint(formData.notes);
  const displayTotalPartResponsabilite =
    prejudicesData[prejudice.type || 'DEPENSE_SANTE_FUTURES']
      .displayTotalPartResponsabilite;
  return [
    ...(notesPrint ? [notesPrint] : []),
    getEmptyLine(),
    ...(table ? table : []),
    ...prejudiceCalculsPrint(prejudice),
    getEmptyLine(),
    getParagraph({
      children: getTextRun({
        text: displayTotalPartResponsabilite
          ? i18next.t(
              'prejudice.prejudicesFormTypes.CALENDRIER_VALEURS.fields.total.print.partResponsabiliteValue',
              {
                partResponsabilite: fPartResponsabilite(
                  partResponsabilite * 100,
                ),
                value: fCurrency(formData.total),
              },
            )
          : i18next.t(
              'prejudice.prejudicesFormTypes.CALENDRIER_VALEURS.fields.total.print.value',
              {
                value: fCurrency(formData.total),
              },
            ),
      }),
    }),
  ];
};
